import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Upload, Star, Pencil, Trash2, ArrowUp, ArrowDown, Loader2, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, imgUrl } from "../../lib/api";
import { CATEGORY_LABELS, SECTION_LABELS } from "../../lib/site";
import { PageTitle, Panel } from "../../components/admin/Field";
import { PhotoEditDialog, Pick } from "../../components/admin/PhotoEditDialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../../components/ui/alert-dialog";

const MAX_MB = 12;
const IconBtn = ({ onClick, children, label, testid, danger }) => (
  <button onClick={onClick} aria-label={label} title={label} data-testid={testid} className={`h-11 w-11 grid place-items-center rounded-full border border-zinc-700 bg-black/40 ${danger ? "hover:border-red-400 hover:text-red-300" : "hover:border-[#D4AF37] hover:text-[#E6C687]"}`}>
    {children}
  </button>
);

const PhotoCard = ({ p, i, total, onMove, onMain, onEdit, onDelete }) => (
  <div data-testid={`admin-photo-item-${i}`} className="bg-[#0A0A0A] border border-zinc-800/80 rounded-lg overflow-hidden">
    <div className="relative aspect-[4/3] bg-[#111111]">
      <img src={imgUrl(p.thumb)} alt={p.title || ""} loading="lazy" className="w-full h-full object-cover" />
      <div className="absolute top-2 left-2 flex flex-wrap gap-1">
        {p.is_main && <span className="text-[10px] uppercase tracking-wider bg-[#D4AF37] text-black px-2 py-0.5 rounded">Principal</span>}
        {p.is_sample && <span className="text-[10px] uppercase tracking-wider bg-zinc-200 text-black px-2 py-0.5 rounded">Exemplo</span>}
        {!p.published && <span className="text-[10px] uppercase tracking-wider bg-zinc-800 text-zinc-300 px-2 py-0.5 rounded flex items-center gap-1"><EyeOff size={10} /> Oculta</span>}
      </div>
    </div>
    <div className="p-3">
      <p className="text-sm truncate text-zinc-200">{p.title || <span className="text-zinc-500">Sem título</span>}</p>
      {p.section === "galeria" && <p className="text-xs text-zinc-500">{CATEGORY_LABELS[p.category]}</p>}
      <div className="flex flex-wrap gap-2 mt-3">
        <IconBtn onClick={() => onEdit(p)} label="Editar" testid={`admin-photo-edit-btn-${i}`}><Pencil size={16} /></IconBtn>
        <IconBtn onClick={() => onMain(p)} label="Definir como principal" testid={`admin-photo-main-btn-${i}`}><Star size={16} className={p.is_main ? "fill-[#D4AF37] text-[#D4AF37]" : ""} /></IconBtn>
        {i > 0 && <IconBtn onClick={() => onMove(i, -1)} label="Mover para cima" testid={`admin-photo-up-btn-${i}`}><ArrowUp size={16} /></IconBtn>}
        {i < total - 1 && <IconBtn onClick={() => onMove(i, 1)} label="Mover para baixo" testid={`admin-photo-down-btn-${i}`}><ArrowDown size={16} /></IconBtn>}
        <IconBtn onClick={() => onDelete(p)} label="Excluir" danger testid={`admin-photo-delete-btn-${i}`}><Trash2 size={16} /></IconBtn>
      </div>
    </div>
  </div>
);

export default function Photos() {
  const [params] = useSearchParams();
  const [section, setSection] = useState(params.get("secao") || "galeria");
  const [accId, setAccId] = useState(params.get("acomodacao") || "");
  const [category, setCategory] = useState("quartos");
  const [rooms, setRooms] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [progress, setProgress] = useState(null);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    api.get("/admin/accommodations").then((r) => {
      setRooms(r.data);
      setAccId((cur) => cur || r.data[0]?.id || "");
    });
  }, []);

  const load = useCallback(async () => {
    if (section === "acomodacao" && !accId) return setPhotos([]);
    const { data } = await api.get("/admin/photos", { params: { section, accommodation_id: section === "acomodacao" ? accId : undefined } });
    setPhotos(data);
  }, [section, accId]);

  useEffect(() => {
    load();
  }, [load]);

  const upload = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    const bad = files.find((f) => f.size > MAX_MB * 1024 * 1024);
    if (bad) return toast.error(`${bad.name} é maior que ${MAX_MB} MB.`);
    let done = 0;
    setProgress({ done, total: files.length });
    try {
      for (let i = 0; i < files.length; i += 4) {
        const fd = new FormData();
        files.slice(i, i + 4).forEach((f) => fd.append("files", f));
        fd.append("section", section);
        fd.append("category", category);
        fd.append("accommodation_id", section === "acomodacao" ? accId : "");
        await api.post("/admin/photos", fd);
        done = Math.min(files.length, i + 4);
        setProgress({ done, total: files.length });
      }
      toast.success(`${files.length} foto(s) enviada(s) e publicada(s).`);
    } catch (e) {
      toast.error(errMsg(e, "Falha no envio."));
    } finally {
      setProgress(null);
      if (inputRef.current) inputRef.current.value = "";
      load();
    }
  };

  const move = async (i, d) => {
    const next = [...photos];
    [next[i], next[i + d]] = [next[i + d], next[i]];
    setPhotos(next);
    await api.post("/admin/photos/reorder", { ids: next.map((p) => p.id) });
  };

  const setMain = async (p) => {
    await api.post(`/admin/photos/${p.id}/main`);
    toast.success("Imagem principal definida");
    load();
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/admin/photos/${deleting.id}`);
      toast.success("Foto excluída");
      setDeleting(null);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div data-testid="admin-photo-manager">
      <PageTitle title="Galeria e fotos" desc="As fotos enviadas aparecem no site imediatamente. Use a estrela para definir a imagem principal." />
      <div className="flex gap-2 overflow-x-auto pb-2 mb-6">
        {Object.entries(SECTION_LABELS).map(([k, l]) => (
          <button key={k} onClick={() => setSection(k)} data-testid={`photos-tab-${k}`} className={`shrink-0 h-11 px-5 rounded-full text-sm border ${section === k ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "border-zinc-700 text-zinc-300"}`}>{l}</button>
        ))}
      </div>
      <Panel testid="photos-upload-panel">
        <div className="grid sm:grid-cols-2 gap-4">
          {section === "acomodacao" && <Pick label="Acomodação" value={accId} onChange={setAccId} options={rooms.map((r) => [r.id, r.name])} testid="photos-room-select" />}
          {section === "galeria" && <Pick label="Categoria das novas fotos" value={category} onChange={setCategory} options={Object.entries(CATEGORY_LABELS)} testid="photos-category-select" />}
        </div>
        <button onClick={() => inputRef.current?.click()} disabled={!!progress || (section === "acomodacao" && !accId)} data-testid="admin-photo-upload-btn" className="btn-gold w-full h-14 text-base disabled:opacity-60">
          {progress ? <><Loader2 className="animate-spin" size={18} /> Enviando {progress.done} de {progress.total}…</> : <><Upload size={18} /> Adicionar fotos</>}
        </button>
        <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp" multiple className="hidden" data-testid="admin-photo-upload-input" onChange={(e) => upload(e.target.files)} />
        <p className="text-xs text-zinc-500">JPG, PNG ou WEBP · até {MAX_MB} MB por foto · várias de uma vez. No iPhone, escolha da Fototeca ou tire uma foto.</p>
      </Panel>
      {section === "acomodacao" && rooms.length === 0 && <p className="mt-6 text-zinc-500">Cadastre uma acomodação antes de enviar fotos para ela.</p>}
      <div className="grid grid-cols-1 min-[420px]:grid-cols-2 lg:grid-cols-3 gap-4 mt-8" data-testid="admin-photo-grid">
        {photos.map((p, i) => <PhotoCard key={p.id} p={p} i={i} total={photos.length} onMove={move} onMain={setMain} onEdit={setEditing} onDelete={setDeleting} />)}
      </div>
      {photos.length === 0 && <p className="mt-8 text-zinc-500" data-testid="photos-empty">Nenhuma foto nesta seção.</p>}
      <PhotoEditDialog photo={editing} rooms={rooms} onClose={() => setEditing(null)} onSaved={load} />
      <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent className="bg-[#0A0A0A] border-zinc-800 w-[92vw]">
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir esta foto?</AlertDialogTitle>
            <AlertDialogDescription>Ela deixará de aparecer no site.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="photo-delete-cancel" className="h-12">Cancelar</AlertDialogCancel>
            <AlertDialogAction data-testid="photo-delete-confirm" onClick={confirmDelete} className="h-12 bg-red-600 hover:bg-red-700 text-white">Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
